/**
 * Organic Traditions — Scroll Animations
 *
 * Reveals direct children of any [data-scroll-animate] section as each
 * child individually enters the viewport. Each child animates on its own
 * scroll moment (no sibling stagger), so a long row of cards animates one
 * by one as the user scrolls past each card.
 *
 * Markup contract (set by snippets/section.liquid):
 *   <div class="section" data-scroll-animate="fade-up | fade-in | slide-left | slide-right | zoom-in">
 *     ...
 *     <div class="section-content-wrapper">
 *       <child/>  <!-- each direct child is observed independently -->
 *     </div>
 *   </div>
 *
 * The matching CSS lives in assets/ot-scroll-animations.css and reads the
 * `.ot-scroll-animate-visible` class added here.
 */
(function () {
  'use strict';

  var THRESHOLD = 0.12;
  var ROOT_MARGIN = '0px 0px -8% 0px';
  var BATCH_STAGGER_MS = 80;

  /**
   * Reveal a list of elements with a left-to-right wave stagger.
   * Elements that entered the viewport in the same observer tick (e.g. items
   * sharing a horizontal row) get an increasing transition-delay so they
   * animate one after another instead of simultaneously. Elements entering
   * in separate ticks animate at their own scroll moment with no delay.
   */
  function revealBatch(targets) {
    if (!targets.length) return;

    targets.sort(function (a, b) {
      return a.getBoundingClientRect().left - b.getBoundingClientRect().left;
    });

    targets.forEach(function (el, index) {
      el.style.transitionDelay = index * BATCH_STAGGER_MS + 'ms';
      requestAnimationFrame(function () {
        el.classList.add('ot-scroll-animate-visible');
      });
    });
  }

  function prefersReducedMotion() {
    return (
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function init() {
    var sections = document.querySelectorAll('[data-scroll-animate]');
    if (!sections.length) return;

    if (prefersReducedMotion() || !('IntersectionObserver' in window)) {
      sections.forEach(function (section) {
        var wrapper = section.querySelector('.section-content-wrapper');
        if (!wrapper) return;
        Array.prototype.forEach.call(wrapper.children, function (child) {
          child.classList.add('ot-scroll-animate-visible');
        });
      });
      return;
    }

    var childObserver = new IntersectionObserver(
      function (entries, obs) {
        var batch = [];
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          batch.push(entry.target);
          obs.unobserve(entry.target);
        });
        revealBatch(batch);
      },
      { threshold: THRESHOLD, rootMargin: ROOT_MARGIN }
    );

    sections.forEach(function (section) {
      var wrapper = section.querySelector('.section-content-wrapper');
      if (!wrapper) return;
      Array.prototype.forEach.call(wrapper.children, function (child) {
        childObserver.observe(child);
      });
    });
  }

  /**
   * Block-level animations: each [data-scroll-animate-block] element
   * animates itself when it enters the viewport (no sibling stagger).
   */
  function initBlocks() {
    var blocks = document.querySelectorAll('[data-scroll-animate-block]');
    if (!blocks.length) return;

    if (prefersReducedMotion() || !('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(blocks, function (el) {
        el.classList.add('ot-scroll-animate-visible');
      });
      return;
    }

    var blockObserver = new IntersectionObserver(
      function (entries, obs) {
        var batch = [];
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          batch.push(entry.target);
          obs.unobserve(entry.target);
        });
        revealBatch(batch);
      },
      { threshold: THRESHOLD, rootMargin: ROOT_MARGIN }
    );

    Array.prototype.forEach.call(blocks, function (el) {
      blockObserver.observe(el);
    });
  }

  function bootAll() {
    init();
    initBlocks();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootAll);
  } else {
    bootAll();
  }

  // Re-run on Shopify theme editor section reloads so newly toggled
  // sections/blocks animate without a full page refresh.
  if (typeof document !== 'undefined') {
    document.addEventListener('shopify:section:load', bootAll);
  }
})();
